<script setup lang="ts">
import { ComboboxItem, ComboboxItemIndicator } from 'reka-ui';
import { computed, useId } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import { cnMerge } from '@/utils/utils.js';
import IconCheck from '~icons/lucide/check';

import { comboboxOptionVariants } from './combobox-variants';
import { injectComboboxContext } from './context';
import KaComboboxIconSlot from './KaComboboxIconSlot.vue';
import type { KaComboboxOptionProps } from './types';

export type { KaComboboxOptionProps } from './types';

const props = defineProps<KaComboboxOptionProps>();
const slots = defineSlots<{
  default?(): any;
  description?(props: { description?: string }): any;
  left?(): any;
  right?(): any;
}>();

const option = computed(() => ({ ...props }));

const comboboxContext = injectComboboxContext();
const comboboxDataTestid = computed(() => comboboxContext?.dataTestid?.value);

// Pass a computed (not a one-time string) so the option's test id tracks runtime changes to the
// parent combobox's dataTestid, keeping the deliberately-reactive injected context intact.
const { dataTestid } = useDataTestid(
  computed(() => `${comboboxDataTestid.value}-option-${option.value.label}`),
);

// reka's ComboboxItem sets no aria-labelledby, so by default its full text content (label AND
// description) becomes the accessible NAME. Mirror KaSelect: label the item from the label <p> only
// (aria-labelledby), and expose the description via aria-describedby so it is announced after the
// label as the option's accessible DESCRIPTION rather than folded into the name.
// These gate BOTH the aria-labelledby/aria-describedby wiring and the matching <p> render below, so
// the referenced IDs only exist when their element does. Use truthiness (not `!= null`) so an empty
// string — which renders no <p> — doesn't leave a dangling ARIA reference.
const hasLabel = computed(() => 'default' in slots || !!option.value.label);
const hasDescription = computed(() => 'description' in slots || !!option.value.description);
const labelId = useId();
const descriptionId = useId();
</script>

<template>
  <!-- Render the option as a native <button>: reka defaults to div[role=option], whose disabled
       state is data-disabled only and isn't announced by assistive tech. A button gets a real
       `disabled`. Force type="button" so options inside a <form> can't submit it (reka's Primitive
       doesn't set a type). appearance-none + text-left undo the button's default chrome/centering. -->
  <ComboboxItem
    as="button"
    type="button"
    :data-testid="dataTestid"
    :name="option.label"
    :value="option"
    :text-value="option.textValue ?? option.label"
    :disabled="option.disabled"
    :aria-labelledby="hasLabel ? labelId : undefined"
    :aria-describedby="hasDescription ? descriptionId : undefined"
    :class="
      cnMerge(
        comboboxOptionVariants({
          withLeadingPadding:
            ('left' in slots || !!option.left) && (!!option.description || 'description' in slots),
        }),
        'appearance-none text-left',
        option.containerClasses,
      )
    "
  >
    <div class="flex w-full items-start justify-between group-data-[disabled]:opacity-50">
      <KaComboboxIconSlot v-if="'left' in slots || option.left" class="mr-2 inline-flex">
        <slot name="left">
          <div
            :class="cnMerge('flex size-6 items-center justify-center', option.left?.wrapperClasses)"
          >
            <component
              :is="option.left?.icon"
              v-if="option.left"
              :class="cnMerge('size-3.5', option.left?.classes)"
            />
          </div>
        </slot>
      </KaComboboxIconSlot>

      <div
        :class="[
          'flex-grow-1 mr-3 flex w-[calc(100%-64px)] flex-col',
          { 'mt-[1px]': 'left' in slots || option.left },
        ]"
      >
        <div :class="cnMerge('flex items-center justify-between', option.labelContainerClasses)">
          <p
            v-if="hasLabel"
            :id="labelId"
            :class="[
              'm-0 flex-1 text-gray-900 group-data-[state=checked]:font-semibold group-data-[state=unchecked]:font-normal dark:text-gray-50',
              { truncate: option.label && !option.label.includes(' ') },
            ]"
            :title="option.label"
          >
            <slot :label="option.label">
              {{ option.label }}
            </slot>
          </p>

          <KaComboboxIconSlot
            v-if="'right' in slots || option.right"
            :icon-options="option.right"
            class="inline-flex"
          >
            <slot name="right" />
          </KaComboboxIconSlot>
        </div>

        <p
          v-if="hasDescription"
          :id="descriptionId"
          :class="[
            'mx-0 mb-0 mt-1 flex text-gray-500 dark:text-gray-400',
            { truncate: option.description && !option.description.includes(' ') },
          ]"
        >
          <slot name="description" v-bind="{ description: option.description }">
            {{ option.description }}
          </slot>
        </p>
      </div>

      <span class="h-5 w-5 flex-shrink-0 flex-grow-0">
        <ComboboxItemIndicator>
          <IconCheck
            class="m-0 h-full w-full p-0 text-gray-900 dark:text-gray-50"
            aria-hidden="true"
          />
        </ComboboxItemIndicator>
      </span>
    </div>
  </ComboboxItem>
</template>
